import { Button } from "hec/components/cta";
import { getDetermineRouteTypeAndUrl } from "hec/hooks/useDetermineRouteTypeAndUrl";
import { type ReactNode, Suspense } from "react";
import { Await } from "react-router";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { type SiteId } from "~/hec/utils/site-id";
import { useRootLayoutData } from "~/root";

import type { ModelReference } from "../BuilderReferenceContent";
import {
  coreButtonInputs,
  hasCustomUrl,
  withCustomUrlInputs,
} from "./cta.helper";
import useFetchPageDataForCta, {
  type BuilderContent,
} from "./useFetchPageDataForCta";

interface QueryParam {
  name: string;
  value: string;
}

interface BuilderLinkWrapperProps {
  children?: ReactNode;
  openInNewTab?: boolean;
  attributes?: {
    className?: string;
    [key: string]: string | number | boolean | undefined;
  };
  clickFunction: string;
  scrollTargetId?: string;
  isInternalLink?: boolean;
  isRevShare?: boolean;
  pageReference?: ModelReference;
  queryParams?: QueryParam[];
  externalLinkReference?: ModelReference;
  customUrl?: string;
  customUrlIsSfra?: boolean;
  "data-testid"?: string;
}

const BuilderLinkWrapper = ({
  children,
  openInNewTab,
  attributes,
  clickFunction,
  scrollTargetId,
  isInternalLink,
  isRevShare,
  pageReference,
  queryParams,
  externalLinkReference,
  customUrl,
  customUrlIsSfra,
  "data-testid": dataTestId,
}: BuilderLinkWrapperProps) => {
  const { className: attrClassName, ...restAttributes } = attributes || {};
  // A non-empty customUrl overrides the page reference — skip the reference
  // fetch entirely (hook still runs unconditionally per the hooks rule).
  const skipReferences = hasCustomUrl(customUrl);
  const { pageRef, externalLinkRef, isResolved } = useFetchPageDataForCta({
    isInternalLink,
    pageReference: skipReferences ? undefined : pageReference,
    externalLinkReference: skipReferences ? undefined : externalLinkReference,
  });
  const { site } = useRootLayoutData();

  // ONE definition of the resolved wrapper, shared by both render paths below —
  // see the twin comment in `link.tsx`. A duplicate copy of this JSX living as
  // the Suspense fallback is what COM-198 shipped twice.
  const renderResolvedWrapper = (
    resolvedPageRef: BuilderContent["data"] | undefined,
    resolvedExternalLinkRef: BuilderContent["data"] | undefined,
  ) => {
    const { routeType, url } = getDetermineRouteTypeAndUrl({
      clickFunction,
      isInternalLink,
      pageReference: resolvedPageRef,
      externalLinkReference: resolvedExternalLinkRef,
      customUrl,
      customUrlIsSfra,
      siteId: site.id as SiteId,
    });

    return (
      <Button
        openInNewTab={openInNewTab}
        clickFunction={clickFunction}
        scrollTargetId={scrollTargetId}
        routeType={routeType ?? undefined}
        url={url!} // TODO: fix this nullish assertion
        queryParams={queryParams}
        className={attrClassName}
        isRevShare={isRevShare}
        {...restAttributes}
        data-testid={dataTestId || "linkWrapper-LinkWrapper"}
      >
        {children}
      </Button>
    );
  };

  // Fast path: reference inlined server-side, nothing to await, no boundary.
  // A typed customUrl also lands here — it skips reference resolution entirely,
  // so both refs are undefined and `isResolved` is true.
  if (isResolved) {
    return renderResolvedWrapper(
      pageRef as BuilderContent["data"] | undefined,
      externalLinkRef as BuilderContent["data"] | undefined,
    );
  }

  // Streaming fallback, unchanged. Full-size disabled Button on purpose — it
  // holds the layout stable during the streaming window and CLS here is 0.000.
  return (
    <Suspense
      fallback={
        <Button
          ctaType="link"
          disabled={true}
          clickFunction=""
          openInNewTab={openInNewTab}
          scrollTargetId={scrollTargetId}
          queryParams={queryParams}
          className={attrClassName}
          isRevShare={isRevShare}
          {...restAttributes}
          data-testid={dataTestId || "linkWrapper-LinkWrapper"}
        >
          {children}
        </Button>
      }
    >
      <Await resolve={pageRef}>
        {resolvedPageRef => (
          <Await resolve={externalLinkRef}>
            {resolvedExternalLinkRef =>
              renderResolvedWrapper(resolvedPageRef, resolvedExternalLinkRef)
            }
          </Await>
        )}
      </Await>
    </Suspense>
  );
};
// BuilderLinkWrapper.displayName = "BuilderLinkWrapper";

export { BuilderLinkWrapper };

export const LinkWrapperDefinition = {
  component: BuilderLinkWrapper,
  name: "LinkWrapper",
  noWrap: true,
  canHaveChildren: true,
  image: "https://unpkg.com/css.gg@2.0.0/icons/svg/link.svg",
  defaultChildren: [
    {
      "@type": "@builder.io/sdk:Element",
      component: {
        name: "Text",
        options: { text: "Click me" },
      },
    },
  ],
  // customUrl inputs spliced in after "isInternalLink" (SFCC-11453); the same
  // helper wires Button and Link (COM-214) so the three CTAs share one editor.
  inputs: withCustomUrlInputs(coreButtonInputs),
} satisfies RegisteredComponent;
